'use client'

import type { CdekTrackingData } from '@/lib/api/schemas/cdek.schema'
import type { CustomerShipmentState } from '@/lib/customer-state'
import { formatCdekStatusName } from '@/lib/customer-state'
import { latestCdekStatus } from '@/lib/cdek-status-history'
import { CDEK_STATUS_TONES, EM_DASH } from '@/lib/order-helpers'
import { FieldRow } from '@/components/ui/FieldRow'
import { CdekStatusCode } from '@/lib/cdek-domain'
import { isCompletedSellerReturn, isFailedReverseDeliveryStatus } from '@/lib/order-returns'

function cdekDate(value?: string, withTime = false): string {
  if (!value) return EM_DASH
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString('ru-RU', withTime
    ? { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }
    : { day: 'numeric', month: 'long', year: 'numeric' })
}

function cdekLocation(location?: CdekTrackingData['to_location']): string {
  if (!location) return EM_DASH
  return [location.city, location.region, location.address].filter(Boolean).join(', ') || EM_DASH
}

export function CdekReturnCard({
  shipment,
  tracking,
  loading,
}: {
  shipment: CustomerShipmentState
  tracking: CdekTrackingData | null
  loading: boolean
}) {
  const current = latestCdekStatus(tracking?.statuses)
  const statusCode = current?.code || shipment.statusCode
  const statusName = shipment.statusName || formatCdekStatusName(statusCode, current?.city)
  const changedAt = current?.date_time || shipment.changedAt
  const cdekNumber = tracking?.cdek_number || shipment.cdekNumber
  const normalizedStatusCode = statusCode?.trim().toUpperCase()
  const completed = current?.code
    ? normalizedStatusCode === CdekStatusCode.Delivered
    : tracking?.delivery_date
      ? true
      : isCompletedSellerReturn(shipment)
  const failed = isFailedReverseDeliveryStatus(normalizedStatusCode)
  const returnedToBuyer = normalizedStatusCode === CdekStatusCode.ReturnedToSender
    || normalizedStatusCode === CdekStatusCode.ReturnedToClient
  const displayStatus = completed
    ? 'Получен на нашем складе'
    : returnedToBuyer
      ? 'Возврат не доставлен · вернулся покупателю'
      : normalizedStatusCode === CdekStatusCode.NotDelivered
        ? 'Возврат пока не доставлен'
        : statusName
  const tone = completed
    ? CDEK_STATUS_TONES[CdekStatusCode.Delivered]
    : failed
      ? 'bg-st-unpaid/10 text-st-unpaid border-st-unpaid/30'
      : CDEK_STATUS_TONES[statusCode || '']
        || 'bg-bone text-ink-muted border-rule-strong'

  return (
    <section className={`surface-card rounded-md mb-8 overflow-hidden border ${completed ? 'border-st-delivered/25' : failed ? 'border-st-unpaid/25' : 'border-gold/25'}`}>
      <div className={`px-4 sm:px-6 py-4 border-b border-rule flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4 ${completed ? 'bg-st-delivered/5' : failed ? 'bg-st-unpaid/5' : 'bg-gold/5'}`}>
        <div>
          <div className={`eyebrow ${completed ? 'text-st-delivered' : failed ? 'text-st-unpaid' : 'text-gold-deep'}`}>
            {completed ? 'Возврат получен на складе' : failed ? 'Проблема возврата' : 'Возврат на наш склад'}
          </div>
          <div className="mt-1 text-[17px] font-semibold tab-nums">{cdekNumber}</div>
          <div className="mt-1 text-[11px] text-ink-muted">
            {completed
              ? 'Обратная доставка завершена — заказ больше не считается активным возвратом'
              : returnedToBuyer
                ? 'Обратная доставка не завершена: посылка снова оказалась у покупателя'
              : 'Отдельная обратная накладная СДЭК'}
          </div>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-1">
          <span className={`px-2.5 py-1 text-[10px] font-semibold border ${tone}`}>
            {displayStatus}
          </span>
          <span className="text-[10px] text-ink-faint tab-nums">
            обновлено {cdekDate(changedAt, true)}
          </span>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-1">
        {tracking ? (
          <>
            <FieldRow
              label="Обратный маршрут"
              value={`${cdekLocation(tracking.from_location)} → ${cdekLocation(tracking.to_location)}`}
            />
            {tracking.planned_delivery_date && (
              <FieldRow label="План возврата" value={cdekDate(tracking.planned_delivery_date)} accent />
            )}
            {tracking.delivery_date && (
              <FieldRow label="Возвращён на склад" value={cdekDate(tracking.delivery_date)} />
            )}
          </>
        ) : (
          <FieldRow
            label="Обратный маршрут"
            value={loading ? 'Загружаем данные обратной накладной…' : 'Подробности маршрута пока недоступны'}
          />
        )}
      </div>

      {tracking?.statuses?.length ? (
        <details className="border-t border-rule">
          <summary className="px-4 sm:px-6 py-3 cursor-pointer text-[11px] font-semibold text-ink-muted hover:text-ink">
            История возврата · {tracking.statuses.length}
          </summary>
          <div className="px-4 sm:px-6 pb-5 max-h-[360px] overflow-y-auto">
            {tracking.statuses.map((status, index) => (
              <div key={`${status.code}-${status.date_time}-${index}`} className="grid grid-cols-1 sm:grid-cols-[115px_1fr_95px] gap-1 sm:gap-3 py-2.5 border-t border-rule/60 text-[11px]">
                <span className="text-ink-faint tab-nums">{cdekDate(status.date_time, true)}</span>
                <span className="font-medium text-ink-2">
                  {formatCdekStatusName(status.code)}
                </span>
                <span className="text-right text-ink-muted truncate">{status.city || EM_DASH}</span>
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </section>
  )
}
